import { defineComponent, ref, computed, watch, watchEffect, provide, openBlock, createElementBlock, createElementVNode, normalizeStyle, normalizeClass, unref, createVNode, createSlots, withCtx, renderSlot, createBlock, Teleport, Transition, withModifiers, createCommentVNode, toDisplayString, nextTick } from "vue";
import { offset, autoPlacement, shift, useFloating, autoUpdate } from "@floating-ui/vue";
import { useEventListener, useResizeObserver } from "@vueuse/core";
import ChevronDownIcon from "../../assets/icons/chevron-down.svg.js";
import SearchIcon from "../../assets/icons/search.svg2.js";
import { useFormControl } from "../../composables/useFormControl.js";
import Input from "../input/Input.vue.js";
import { SELECT_KEY } from "./type.js";
const _hoisted_1 = {
  key: 0,
  class: "select__scroll"
};
const _hoisted_2 = ["value", "placeholder"];
const _hoisted_3 = { class: "select__options" };
const _hoisted_4 = {
  key: 0,
  class: "select__options-empty"
};
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "Select",
  props: {
    multiple: { type: Boolean, default: false },
    modelValue: {},
    size: { default: "sm" },
    wrapperClass: { default: "" },
    readonly: { type: Boolean, default: false },
    placeholder: {},
    disabled: { type: Boolean, default: false },
    rounded: {},
    width: {},
    truncate: { type: Boolean },
    options: { default: () => [] },
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { default: "Search..." },
    emptyText: {}
  },
  emits: ["update:modelValue"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const reference = ref(null);
    const referenceWidth = ref(null);
    const floating = ref(null);
    const allOptions = ref([]);
    const filteredOptions = ref([]);
    const optionsOpen = ref(false);
    const inputValue = ref(void 0);
    const { model: formControlModel } = useFormControl(props, emit);
    const onEnter = (el) => {
      el.style.opacity = "0";
      requestAnimationFrame(() => {
        el.style.transition = "opacity 0.2s ease";
        el.style.opacity = "1";
      });
    };
    const onLeave = (el) => {
      requestAnimationFrame(() => {
        el.style.transition = "opacity 0.15s ease";
        el.style.opacity = "0";
      });
    };
    const handleOptionSelected = (option) => {
      if (props.multiple) {
        if (Array.isArray(selectedOptions.value)) {
          if (selectedOptions.value.some((item) => item.value === option.value)) {
            handleOptionRemoved(option);
            return;
          }
          selectedOptions.value = [...selectedOptions.value, option];
          return;
        }
        return selectedOptions.value = [option];
      }
      selectedOptions.value = option;
      handleOptionsClose();
    };
    const handleOptionRemoved = (option) => {
      if (props.multiple) {
        if (Array.isArray(selectedOptions.value)) {
          selectedOptions.value = selectedOptions.value.filter((item) => item.value !== option.value);
        }
        return;
      }
      return selectedOptions.value = void 0;
    };
    const handleOptionsToggle = () => {
      if (isSelectInteractionLocked.value) return;
      if (optionsOpen.value) {
        handleOptionsClose();
      } else {
        handleOptionsOpen();
      }
    };
    const handleOptionsOpen = async () => {
      optionsOpen.value = true;
      await nextTick();
      syncFilteredOptionsFromRegistry();
    };
    const handleOptionsClose = () => {
      optionsOpen.value = false;
      dropdownSearchQuery.value = "";
      void nextTick(() => {
        syncOptionsFromProps(props.options);
      });
    };
    const dropdownSearchQuery = ref("");
    const handleDropdownSearch = (query) => {
      dropdownSearchQuery.value = query;
      const q = query.toLocaleLowerCase().trim();
      if (!q) {
        filteredOptions.value = allOptions.value;
        return;
      }
      filteredOptions.value = allOptions.value.filter((item) => {
        if (item.label.toLocaleLowerCase().includes(q)) return true;
        if (String(item.value).toLocaleLowerCase().includes(q)) return true;
        if (item.description?.toLocaleLowerCase().includes(q)) return true;
        if (item.searchText?.toLocaleLowerCase().includes(q)) return true;
        return false;
      });
    };
    const handleOptionsAdd = (option) => {
      if (!allOptions.value.some((o) => o.value === option.value)) {
        filteredOptions.value.push(option);
        allOptions.value.push(option);
      }
    };
    const handleOptionsUnregister = (option) => {
      filteredOptions.value = filteredOptions.value.filter((o) => o.value !== option.value);
      allOptions.value = allOptions.value.filter((o) => o.value !== option.value);
    };
    const handleInputChange = (label) => {
      if (props.readonly) {
        return;
      }
      inputValue.value = label;
      const searchLabel = label.toLocaleLowerCase().trim();
      if (!searchLabel) return filteredOptions.value = allOptions.value;
      filteredOptions.value = allOptions.value.filter((item) => {
        const itemLabel = item.label.toLocaleLowerCase().trim();
        return itemLabel.includes(searchLabel);
      });
    };
    const isSelectInteractionLocked = computed(() => props.disabled);
    const middleware = computed(() => {
      return [offset(4), autoPlacement({ allowedPlacements: ["top", "bottom"] }), shift()];
    });
    const selectedOptions = computed({
      get: () => {
        if (props.modelValue) return props.modelValue;
        return formControlModel?.value;
      },
      set: (val) => {
        if (formControlModel) {
          formControlModel.value = val;
        } else {
          emit("update:modelValue", val);
        }
      }
    });
    const inputContentType = computed(() => {
      if (props.multiple) {
        return "tags";
      }
      return void 0;
    });
    const { floatingStyles } = useFloating(reference, floating, {
      placement: "bottom-start",
      middleware,
      whileElementsMounted: autoUpdate
    });
    function syncOptionsFromProps(options) {
      allOptions.value = [...options];
      syncFilteredOptionsFromRegistry();
    }
    function syncFilteredOptionsFromRegistry() {
      if (dropdownSearchQuery.value.trim().length > 0) {
        handleDropdownSearch(dropdownSearchQuery.value);
        return;
      }
      filteredOptions.value = [...allOptions.value];
    }
    watch(
      () => props.options,
      (options) => {
        syncOptionsFromProps(options);
      },
      { immediate: true, deep: true }
    );
    watchEffect(() => {
      if (props.multiple && Array.isArray(selectedOptions.value)) {
        return inputValue.value = selectedOptions.value?.map((item) => item.label);
      }
      if (!Array.isArray(selectedOptions.value)) {
        return inputValue.value = selectedOptions.value?.label || "";
      }
      return inputValue.value = "";
    });
    watch(isSelectInteractionLocked, (locked) => {
      if (locked) handleOptionsClose();
    });
    useEventListener(
      document,
      "pointerdown",
      (e) => {
        if (!optionsOpen.value) return;
        const target = e.target;
        if (reference.value?.contains(target)) return;
        if (floating.value?.contains(target)) return;
        handleOptionsClose();
      },
      { capture: true }
    );
    useResizeObserver(reference, (entries) => {
      const entry = entries[0];
      if (entry) {
        referenceWidth.value = entry.target.offsetWidth;
      }
    });
    provide(SELECT_KEY, {
      onOptionSelected: handleOptionSelected,
      onOptionRemoved: handleOptionsUnregister,
      onOptionsAdded: handleOptionsAdd,
      selectedOptions,
      filteredOptions
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", null, [
        createElementVNode("div", {
          class: normalizeClass(["select", __props.wrapperClass]),
          style: normalizeStyle(__props.width ? { width: __props.width, maxWidth: __props.width } : {})
        }, [
          createElementVNode("div", {
            ref_key: "reference",
            ref: reference,
            class: normalizeClass(["select__input", { "select__input--locked": unref(isSelectInteractionLocked) }]),
            onClick: handleOptionsToggle
          }, [
            createVNode(Input, {
              "content-type": unref(inputContentType),
              "model-value": unref(inputValue),
              size: __props.size,
              disabled: __props.disabled,
              readonly: __props.readonly,
              rounded: __props.rounded,
              placeholder: __props.placeholder,
              "text-input-class": [__props.truncate ? "truncate" : ""],
              "onUpdate:modelValue": handleInputChange
            }, createSlots({
              suffix: withCtx(() => [
                renderSlot(_ctx.$slots, "suffix", {}, () => [
                  createVNode(unref(ChevronDownIcon), {
                    class: normalizeClass(["w-[10px] h-[6px] transition-transform duration-300", { "transform rotate-180": unref(optionsOpen) }])
                  }, null, 8, ["class"])
                ], true)
              ]),
              _: 2
            }, [
              _ctx.$slots.prefix ? {
                name: "prefix",
                fn: withCtx(() => [
                  renderSlot(_ctx.$slots, "prefix", {}, void 0, true)
                ]),
                key: "0"
              } : void 0
            ]), 1032, ["content-type", "model-value", "size", "disabled", "readonly", "rounded", "placeholder", "text-input-class"])
          ], 2),
          (openBlock(), createBlock(Teleport, { to: "body" }, [
            createElementVNode("div", {
              ref_key: "floating",
              ref: floating,
              class: "select__floating",
              style: normalizeStyle([
                unref(floatingStyles),
                {
                  width: unref(referenceWidth) ? unref(referenceWidth) + "px" : "auto",
                  minInlineSize: unref(referenceWidth) ? unref(referenceWidth) + "px" : "auto",
                  zIndex: 9999
                }
              ])
            }, [
              createVNode(Transition, {
                onEnter: _cache[2] || (_cache[2] = (el) => onEnter(el)),
                onLeave: _cache[3] || (_cache[3] = (el) => onLeave(el))
              }, {
                default: withCtx(() => [
                  unref(optionsOpen) ? (openBlock(), createElementBlock("div", _hoisted_1, [
                    __props.searchable ? (openBlock(), createElementBlock("div", {
                      key: 0,
                      class: "select__search-wrapper",
                      onClick: _cache[1] || (_cache[1] = withModifiers(() => {
                      }, ["stop"]))
                    }, [
                      createVNode(unref(SearchIcon), { class: "select__search-icon" }),
                      createElementVNode("input", {
                        value: unref(dropdownSearchQuery),
                        class: "select__search-input",
                        placeholder: __props.searchPlaceholder,
                        autocomplete: "off",
                        onInput: _cache[0] || (_cache[0] = (e) => handleDropdownSearch(e.target.value))
                      }, null, 40, _hoisted_2)
                    ])) : createCommentVNode("", true),
                    createElementVNode("div", _hoisted_3, [
                      renderSlot(_ctx.$slots, "default", {}, void 0, true),
                      unref(filteredOptions).length === 0 ? (openBlock(), createElementBlock("div", _hoisted_4, toDisplayString(__props.emptyText ?? "No options found"), 1)) : createCommentVNode("", true)
                    ])
                  ])) : createCommentVNode("", true)
                ]),
                _: 3
              })
            ], 4)
          ]))
        ], 6)
      ]);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=Select.vue2.js.map
